import { InputField } from "@/components/shared";
import { CircleLoader } from "@/components/shared/loaders";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { useUpdateCustomerNameMutationHook } from "@/hooks";
import { useTenderPermissionsData } from "@/hooks/cache";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { zodResolver } from "@hookform/resolvers/zod";
import { TFunction } from "i18next";
import { Info, PencilIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { z } from "zod";

/**
 * Create a form schema for updating the customer name of a tender
 * @param t - The translation function
 * @returns A form schema for updating the customer name of a tender
 */
const createUpdateCustomerFormSchema = (t: TFunction) => {
  return z.object({
    customerName: z.string().min(1, {
      message: t("schemas:form.required"),
    }),
  });
};

/**
 * Update the customer name of a tender
 * @returns A dialog to update the customer name of a tender
 */
function UpdateCustomerNameDialog() {
  const { updateCustomerNameMutation, loading } = useUpdateCustomerNameMutationHook();

  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const {
    fetchTenderById: {
      id: tenderId,
      customer: { name: customerName } = {}
    } = {}
  } = useOutletContext<EditTenderContext>().tenderByIdData || {};

  const { canEditDetails } = useTenderPermissionsData(Number(tenderId));

  const formSchema = createUpdateCustomerFormSchema(t);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      customerName: customerName ?? "",
    },
  });

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset({ customerName: customerName ?? "" });
    }
    setIsOpen(open);
  };

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    updateCustomerNameMutation({
      variables: {
        tenderId: Number(tenderId),
        name: data.customerName,
      },
    });
    setIsOpen(false);
  };

  if (!canEditDetails) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="xs">
          <PencilIcon className="w-2 h-2 mr-1" /> {t("common:buttons.edit")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <DialogHeader>
              <DialogTitle>{t("modals:updatecustomername.title")}</DialogTitle>
              <DialogDescription>{t("modals:updatecustomername.description", { customerName })}</DialogDescription>
            </DialogHeader>
            <Card className="p-2 text-xs flex items-start gap-2 shadow-none">
              <Info className="w-4 h-4 shrink-0" />
              <p>
                {t("modals:updatecustomername.disclaimer")}
              </p>
            </Card>
            <InputField
              form={form}
              name="customerName"
              label={t("modals:updatecustomername.inputs.customerName.label")}
              placeholder={t("modals:updatecustomername.inputs.customerName.placeholder")}
              className="w-full"
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" type="button" disabled={loading}>{t("common:buttons.cancel")}</Button>
              </DialogClose>
              <Button variant="default" type="submit" disabled={loading} className="gap-2">
                <CircleLoader visible={loading} height={16} width={16} strokeWidth={2} />
                {t("common:buttons.save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default UpdateCustomerNameDialog;
